import { useState } from 'react';
import { ChevronRight, CheckCircle2, Loader2 } from 'lucide-react';
import ChatMessageMarkdown from './ChatMessageMarkdown.jsx';

/**
 * Inline, collapsible record of everything a workflow run did (like a
 * terminal's "Ran N commands ˅"): one-line transient progress steps
 * (kind 'progress') and the run's intermediate messages — brief questions,
 * QA replies, review notices — as full markdown blocks (kind 'milestone').
 * While the run is live the header shows the latest step text with a running
 * indicator; once the run completes it freezes to a static summary. The
 * group stays in the thread so the run's story is scrollable later; only the
 * run's final message renders as a regular bubble after it. Presentational
 * only — all copy is passed in (already localized).
 *
 * @param {object} props
 * @param {Array<{ id?: string, text?: string, kind?: 'progress'|'milestone' }>} [props.steps]
 * @param {boolean} [props.isRunning]
 * @param {string} [props.runningFallback] — header text while running when the latest step has no text.
 * @param {string} [props.summaryText] — header text once frozen (e.g. "Ran 5 steps").
 * @param {string} [props.toggleLabel] — accessible label for the expand/collapse control.
 * @param {string} [props.maxWidthClass] — width clamp matching sibling bubbles.
 */
const RunActivityGroup = ({
  steps = [],
  isRunning = false,
  runningFallback = 'Working…',
  summaryText = '',
  toggleLabel = 'Toggle activity steps',
  maxWidthClass = '',
}) => {
  const [expanded, setExpanded] = useState(false);
  // The header line stays a one-liner: prefer the latest PROGRESS step so a
  // long milestone body never floods the collapsed row.
  const latestProgress = [...steps].reverse().find((s) => s.kind !== 'milestone');
  const latestText = (latestProgress ?? steps[steps.length - 1])?.text ?? '';
  const headerText = isRunning ? latestText || runningFallback : summaryText || runningFallback;

  return (
    <div className={`w-full min-w-0 ${maxWidthClass} overflow-hidden rounded-xl border border-edge bg-surface-muted`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={toggleLabel}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-hover"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-content-muted transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        {isRunning ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-500" />
        ) : (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-content-muted" />
        )}
        <span
          className={`min-w-0 flex-1 truncate text-sm ${isRunning ? 'text-content' : 'text-content-secondary'}`}
          aria-live={isRunning ? 'polite' : undefined}
        >
          {headerText}
        </span>
        {steps.length ? (
          <span className="shrink-0 rounded-full bg-surface px-1.5 py-0.5 text-xs text-content-muted">
            {steps.length}
          </span>
        ) : null}
      </button>

      {expanded && steps.length ? (
        <ol className="space-y-1.5 border-t border-edge/60 px-3 py-2">
          {steps.map((s, i) => (
            <li key={s.id ?? `step-${s.kind || 'step'}-${i}`} className="flex gap-2 text-xs text-content-secondary">
              <span className="shrink-0 select-none text-content-muted">{i + 1}.</span>
              {s.kind === 'milestone' ? (
                // A full message the workflow sent mid-run (brief questions,
                // QA reply, review notice) — rendered as markdown in a quiet
                // sub-block so the run's story reads in place.
                <div className="min-w-0 flex-1 rounded-lg border border-edge/60 bg-surface px-2.5 py-2">
                  <ChatMessageMarkdown content={s.text || ''} />
                </div>
              ) : (
                <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{s.text}</span>
              )}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
};

export default RunActivityGroup;
